/** Session-package previews use explicit units: profile prices in HKD dollars,
 * doctors.session_price in cents. Checkout uses the same source precedence.
 */

// The three built-in packages MindVault falls back to when a therapist has no
// custom packages. Seeded locally when the therapist first opens the editor —
// nothing is written to the DB until they save.
export const DEFAULT_PACKAGE_SEEDS = [
  { name: 'Taster', sessions: 1, discount_pct: 0, addons: [] },
  { name: 'Experience', sessions: 4, discount_pct: 10, addons: [] },
  { name: 'Transform', sessions: 6, discount_pct: 15, addons: [] },
];

export const MAX_NAME_LENGTH = 60;
export const MAX_ADDON_TITLE_LENGTH = 80;
export const MIN_SESSIONS = 1;
export const MAX_SESSIONS = 20;
export const MIN_DISCOUNT_PCT = 0;
export const MAX_DISCOUNT_PCT = 50;

/** Profile values are HKD dollars; doctors.session_price is explicitly cents. */
export const parsePriceToCents = (priceValue, unit = 'hkd') => {
  if (priceValue === null || priceValue === undefined || typeof priceValue === 'boolean') return null;
  const cleaned = String(priceValue).replace(/[$,\s]/g, '');
  if (!cleaned) return null;
  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.round((parsed + Number.EPSILON) * (unit === 'cents' ? 1 : 100));
};

/** Package price in cents: base × sessions × (1 − discount%), truncated like the backend. */
export const computePackagePriceCents = (basePriceCents, sessions, discountPct) => {
  const base = Number(basePriceCents);
  const sessionCount = Number(sessions);
  const discount = Number(discountPct);
  if (!Number.isFinite(base) || base <= 0) return null;
  if (!Number.isInteger(sessionCount) || sessionCount < MIN_SESSIONS || sessionCount > MAX_SESSIONS) return null;
  if (!Number.isInteger(discount) || discount < MIN_DISCOUNT_PCT || discount > MAX_DISCOUNT_PCT) return null;
  return Math.trunc((base * sessionCount * (100 - discount)) / 100);
};

export const formatHKD = (cents) => {
  const value = Number(cents || 0) / 100;
  return `HK$ ${value.toLocaleString('en-HK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** Accessible price summary for package previews. */
export const formatPackagePreview = (basePriceCents, sessions, discountPct) => {
  const priceCents = computePackagePriceCents(basePriceCents, sessions, discountPct);
  if (priceCents === null) return null;
  const fullPriceCents = Number(basePriceCents) * Number(sessions);
  return `Regular ${formatHKD(fullPriceCents)} → Package ${formatHKD(priceCents)} · Save ${formatHKD(fullPriceCents - priceCents)} (${Number(discountPct)}% off)`;
};

/** Field-level validation for one package draft. Returns {field: message}; {} means valid. */
export const validatePackageDraft = (draft) => {
  const errors = {};
  const name = (draft?.name || '').trim();
  if (!name) {
    errors.name = 'Name is required';
  } else if (name.length > MAX_NAME_LENGTH) {
    errors.name = `Name must be ${MAX_NAME_LENGTH} characters or fewer`;
  }

  const sessions = Number(draft?.sessions);
  if (!Number.isInteger(sessions) || sessions < MIN_SESSIONS || sessions > MAX_SESSIONS) {
    errors.sessions = `Sessions must be a whole number between ${MIN_SESSIONS} and ${MAX_SESSIONS}`;
  }

  const discount = draft?.discount_pct === '' ? NaN : Number(draft?.discount_pct);
  if (!Number.isInteger(discount) || discount < MIN_DISCOUNT_PCT || discount > MAX_DISCOUNT_PCT) {
    errors.discount_pct = `Discount must be a whole number between ${MIN_DISCOUNT_PCT} and ${MAX_DISCOUNT_PCT}`;
  }

  (draft?.addons || []).forEach((addon, index) => {
    const title = (addon?.title || '').trim();
    if (!title) {
      errors[`addon-${index}-title`] = 'Add-on title is required';
    } else if (title.length > MAX_ADDON_TITLE_LENGTH) {
      errors[`addon-${index}-title`] = `Add-on title must be ${MAX_ADDON_TITLE_LENGTH} characters or fewer`;
    }
  });

  return errors;
};

export const isPackageDraftValid = (draft) => Object.keys(validatePackageDraft(draft)).length === 0;
